/*
 * Purpose: Client-side workflow execution engine for triggers, tools, and actions
 * Last modified: 2026-06-25 | Agent
 * Completeness: 88
 */
import { Workflow, Node } from '@/stores/workflowStore';
import { substituteSecrets } from '@/lib/resolveSecret';
import { useLogStore } from '@/stores/logStore';
import { fetchLatestGmailEvent } from '@/services/connectors/gmailInbound';
import { fetchLatestGmailApiMessage } from '@/services/connectors/gmailApi';
import { brandedBuildStatus, getBrandedBlockDef } from '@/lib/brandedBlocks';
import { invokeMcpServer } from '@/services/connectors/mcp/mcpApi';
import { getMcpAuthToken, getMcpServerUrl } from '@/services/connectors/mcp/mcpConnection';

export interface ExecutionContext {
  nodeId: string;
  input: any;
  output: any;
  errors: string[];
  timestamp: Date;
  dryRun?: boolean;
  simulated?: boolean;
}

export interface ExecutionOptions {
  dryRun?: boolean;
  /** Injected at trigger when dry-running (e.g. sample email payload) */
  triggerFixture?: Record<string, unknown>;
  /** Skip writing to localStorage during dry run */
  skipPersistence?: boolean;
  /** When aborted, execution stops before the next node */
  signal?: AbortSignal;
  onNodeStart?: (nodeId: string) => void;
  onNodeComplete?: (nodeId: string) => void;
  onNodeError?: (nodeId: string, message: string) => void;
}

export class WorkflowExecutor {
  private workflow: Workflow;
  private logStore: ReturnType<typeof useLogStore.getState>;
  private context: Map<string, ExecutionContext> = new Map();
  private options: ExecutionOptions;

  constructor(workflow: Workflow, options: ExecutionOptions = {}) {
    this.workflow = workflow;
    this.options = options;
    this.logStore = useLogStore.getState();
    const mode = options.dryRun ? 'dry run' : 'live';
    this.logStore.addLog('info', `Starting workflow (${mode}): ${workflow.name}`, workflow.id);
  }

  async execute(): Promise<ExecutionContext[]> {
    this.assertNotAborted();

    const entryNodes = this.workflow.nodes.filter(
      (n) => !this.workflow.edges.some((e) => e.to === n.id)
    );

    if (entryNodes.length === 0) {
      this.logStore.addLog('error', 'No entry nodes in workflow', this.workflow.id);
      throw new Error('No entry nodes in workflow');
    }

    const orderedEntries = [
      ...entryNodes.filter((n) => n.type === 'start'),
      ...entryNodes.filter((n) => n.type === 'goal'),
      ...entryNodes.filter((n) => n.type !== 'start' && n.type !== 'goal'),
    ];

    for (const entry of orderedEntries) {
      await this.executeNode(entry, null);
    }

    this.logStore.addLog('success', `Workflow "${this.workflow.name}" completed`, this.workflow.id);

    return Array.from(this.context.values());
  }

  private assertNotAborted(): void {
    if (this.options.signal?.aborted) {
      throw new DOMException('Flow execution stopped', 'AbortError');
    }
  }

  private async executeNode(node: Node, input: any): Promise<any> {
    this.assertNotAborted();
    this.options.onNodeStart?.(node.id);
    try {
      let output = input;

      switch (node.type) {
        case 'start':
          output = this.executeStart(node);
          this.logStore.addLog('info', 'Mission START block entered', this.workflow.id);
          break;
        case 'goal':
          output = this.executeGoal(node);
          this.logStore.addLog('info', `Goal anchored: ${node.config.outcome || 'outcome'}`, this.workflow.id);
          break;
        case 'agent':
          output = this.executeAgent(node, input);
          this.logStore.addLog('info', `Agent block: ${node.config.contextLabel || node.subtype}`, this.workflow.id);
          break;
        case 'drone':
          output = await this.executeDrone(node, input);
          this.logStore.addLog('info', `Drone block: ${node.config.contextLabel || node.subtype}`, this.workflow.id);
          break;
        case 'tube':
          output = this.executeJourneyTube(node, input);
          this.logStore.addLog('info', 'Journey Tube — path toward outcome', this.workflow.id);
          break;
        case 'branded':
          output = this.executeBranded(node, input);
          this.logStore.addLog('info', `Branded block: ${node.config.contextLabel || node.subtype}`, this.workflow.id);
          break;
        case 'trigger':
          output = await this.executeTrigger(node);
          this.logStore.addLog('info', `Trigger executed: ${node.subtype}`, this.workflow.id);
          break;
        case 'tool':
          output = await this.executeTool(node, input);
          this.logStore.addLog('info', `Tool executed: ${node.subtype}`, this.workflow.id);
          break;
        case 'action':
          output = await this.executeAction(node, input);
          this.logStore.addLog('success', `Action executed: ${node.subtype}`, this.workflow.id);
          break;
      }

      // Store context
      this.context.set(node.id, {
        nodeId: node.id,
        input,
        output,
        errors: [],
        timestamp: new Date(),
        dryRun: this.options.dryRun,
        simulated: Boolean(output?.simulated || output?.dryRun),
      });
      this.options.onNodeComplete?.(node.id);

      // Execute connected nodes (conditional nodes branch on first/second edge)
      const connectedEdges = this.workflow.edges.filter((e) => e.from === node.id);
      const edgesToRun =
        node.type === 'tool' && node.subtype === 'conditional'
          ? output != null
            ? connectedEdges.slice(0, 1)
            : connectedEdges.slice(1, 2)
          : connectedEdges;

      for (const edge of edgesToRun) {
        this.assertNotAborted();
        const nextNode = this.workflow.nodes.find((n) => n.id === edge.to);
        if (nextNode) {
          await this.executeNode(nextNode, output);
        }
      }

      return output;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        this.logStore.addLog('warning', 'Flow execution stopped by user', this.workflow.id);
        throw error;
      }
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      this.logStore.addLog('error', errorMsg, this.workflow.id);
      this.options.onNodeError?.(node.id, errorMsg);
      this.context.set(node.id, {
        nodeId: node.id,
        input,
        output: null,
        errors: [errorMsg],
        timestamp: new Date(),
      });
      throw error;
    }
  }

  private executeStart(node: Node): Record<string, unknown> {
    const mapLocked = this.workflow.missionMeta?.mapLocked === true;
    return {
      type: 'start',
      subtype: node.subtype,
      startedAt: new Date().toISOString(),
      name: node.config.name,
      mapLocked,
      dynamicMap: !mapLocked,
    };
  }

  private executeGoal(node: Node): Record<string, unknown> {
    const outcome = String(node.config.outcome ?? '').trim();
    return {
      type: 'goal',
      subtype: node.subtype,
      outcome,
      identified: Boolean(outcome),
      name: node.config.name,
      contextPurpose: node.config.contextPurpose,
    };
  }

  private executeAgent(node: Node, input: unknown): Record<string, unknown> {
    const brief = String(node.config.agentBrief ?? node.config.contextPurpose ?? '').trim();
    const autonomy = String(node.config.autonomy ?? 'supervised');
    if (this.options.dryRun) {
      return {
        type: 'agent',
        subtype: node.subtype,
        brief,
        autonomy,
        contextLabel: node.config.contextLabel,
        simulated: true,
        dryRun: true,
        input,
        decision: '[Dry run] Agent would reason and delegate per contextual configuration.',
      };
    }
    return {
      type: 'agent',
      subtype: node.subtype,
      brief,
      autonomy,
      contextLabel: node.config.contextLabel,
      input,
      decision: brief
        ? `Agent brief acknowledged: ${brief.slice(0, 120)}`
        : 'Agent awaiting contextual configuration (brief / purpose).',
      delegated: false,
    };
  }

  private async executeDrone(node: Node, input: unknown): Promise<Record<string, unknown>> {
    const task = String(node.config.droneTask ?? node.config.contextPurpose ?? '').trim();
    const target = String(node.config.remoteTarget ?? '').trim();
    if (this.options.dryRun) {
      return {
        type: 'drone',
        subtype: node.subtype,
        task,
        target: target || undefined,
        contextLabel: node.config.contextLabel,
        simulated: true,
        dryRun: true,
        input,
        result: '[Dry run] Drone would execute remotely per contextual configuration.',
      };
    }
    return {
      type: 'drone',
      subtype: node.subtype,
      task,
      target: target || undefined,
      contextLabel: node.config.contextLabel,
      input,
      result: task
        ? `Drone task queued: ${task.slice(0, 120)}`
        : 'Drone awaiting contextual configuration (task / purpose).',
      completed: Boolean(task),
    };
  }

  private executeJourneyTube(node: Node, input: unknown): Record<string, unknown> {
    const phases = (node.config.journeyPhases as string[]) ?? [];
    return {
      type: 'tube',
      subtype: node.subtype,
      journey: true,
      phases,
      contextLabel: node.config.contextLabel,
      input,
      message: 'Journey Tube open — outcome may unfold naturally; blocks attach along this path.',
      dryRun: this.options.dryRun,
    };
  }

  private executeBranded(node: Node, input: unknown): Record<string, unknown> {
    const def = getBrandedBlockDef(node.subtype);
    const status = brandedBuildStatus(node.subtype, node.config);
    const brand = def?.brand ?? 'Branded';

    if (status === 'idea') {
      return {
        type: 'branded',
        subtype: node.subtype,
        brand,
        buildStatus: 'idea',
        simulated: true,
        skipped: true,
        input,
        message: `${brand} block is an idea — Director will build when contextual configuration has a clear purpose.`,
      };
    }

    return {
      type: 'branded',
      subtype: node.subtype,
      brand,
      buildStatus: 'ready',
      contextLabel: node.config.contextLabel,
      contextPurpose: node.config.contextPurpose,
      input,
      simulated: true,
      message: `[Placeholder] ${brand} integration not shipped yet — purpose is clear; awaiting product wiring.`,
    };
  }

  private async executeTrigger(node: Node): Promise<any> {
    const { subtype, config } = node;

    switch (subtype) {
      case 'manual':
        if (this.options.dryRun && this.options.triggerFixture) {
          return { ...this.options.triggerFixture, type: 'manual', dryRun: true };
        }
        return { triggeredAt: new Date(), type: 'manual' };

      case 'webhook':
        if (this.options.dryRun && this.options.triggerFixture) {
          return { ...this.options.triggerFixture, type: 'webhook', dryRun: true };
        }
        if (!config.url) throw new Error('Webhook URL not configured');
        return { url: config.url, type: 'webhook' };

      case 'gmail_inbound':
        if (this.options.dryRun && this.options.triggerFixture) {
          return { ...this.options.triggerFixture, type: 'gmail_inbound', dryRun: true };
        }
        return this.executeGmailInboundTrigger(config);

      case 'timer':
        if (!config.interval) throw new Error('Timer interval not configured');
        return { interval: config.interval, type: 'timer' };

      case 'api':
        return { type: 'api' };

      default:
        throw new Error(`Unknown trigger type: ${subtype}`);
    }
  }

  private async executeTool(node: Node, input: any): Promise<any> {
    const { subtype, config } = node;

    switch (subtype) {
      case 'http':
        return this.executeHttpTool(config, input);

      case 'transform':
        return this.executeTransformTool(config, input);

      case 'conditional':
        return this.executeConditionalTool(config, input);

      case 'delay':
        if (!this.options.dryRun) {
          await new Promise((resolve) =>
            setTimeout(resolve, (config.duration || 1000) as number)
          );
        }
        return { ...input, delaySkipped: this.options.dryRun };

      case 'mcp_tool':
        return this.executeMcpTool(config, input);

      default:
        throw new Error(`Unknown tool type: ${subtype}`);
    }
  }

  private async executeHttpTool(config: any, input: any): Promise<any> {
    const url = substituteSecrets((config.url || '').trim());
    const method = config.method || 'GET';

    if (!url) {
      return { skipped: true, reason: 'No URL configured', input };
    }

    let headers = {};

    if (config.headers) {
      try {
        const headerStr = substituteSecrets(
          typeof config.headers === 'string' ? config.headers : JSON.stringify(config.headers)
        );
        headers = JSON.parse(headerStr);
      } catch (e) {
        console.warn('Failed to parse headers', e);
      }
    }

    if (this.options.dryRun) {
      return {
        simulated: true,
        dryRun: true,
        url,
        method,
        message: 'HTTP call simulated during dry run — no external API invoked.',
        requestBody: input,
        response: this.simulatedHttpResponse(url),
      };
    }

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...headers },
        body: method !== 'GET' && method !== 'HEAD' ? JSON.stringify(input) : undefined,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        return response.json();
      }
      return { status: response.status, body: await response.text() };
    } catch (error) {
      if (url.includes('example.com') || url.includes('api.linkedin.com') || url.includes('openai.com')) {
        return {
          simulated: true,
          url,
          message: 'Endpoint simulated (unreachable or dry-run fallback)',
          input,
        };
      }
      throw error;
    }
  }

  private async executeMcpTool(config: Record<string, unknown>, input: unknown): Promise<unknown> {
    const mcpServerId = String(config.mcpServerId ?? config.serverId ?? 'default');
    const capability = String(
      config.capabilityId ?? config.mcpCapability ?? config.action ?? ''
    ).toLowerCase();
    const listTools =
      capability.includes('list_tools') ||
      /\blist\b.*\btools?\b/i.test(String(config.name ?? ''));
    const toolName = String(config.toolName ?? config.tool ?? '').trim();

    const serverUrl = getMcpServerUrl(mcpServerId);
    const authToken = getMcpAuthToken(mcpServerId);

    if (this.options.dryRun) {
      return {
        simulated: true,
        dryRun: true,
        mcpServerId,
        mcpGoal: config.mcpGoal ?? config.blockIntent,
        action: listTools ? 'tools/list' : 'tools/call',
        toolName: listTools ? undefined : toolName || String(config.mcpToolName ?? '(tool not set)'),
        message: 'MCP call simulated during dry run — no external server invoked.',
        input,
      };
    }

    if (!serverUrl) {
      throw new Error('MCP server URL not configured — open Connect → MCP');
    }

    if (listTools) {
      const response = await invokeMcpServer(serverUrl, 'tools/list', {}, authToken);
      if (!response.ok) {
        throw new Error(response.error || 'MCP tools/list failed');
      }
      return response.result;
    }

    if (!toolName) {
      throw new Error('MCP tool name not configured on this block');
    }

    let toolArgs: Record<string, unknown> = {};
    if (config.arguments && typeof config.arguments === 'object') {
      toolArgs = config.arguments as Record<string, unknown>;
    } else if (typeof config.arguments === 'string' && config.arguments.trim()) {
      try {
        toolArgs = JSON.parse(substituteSecrets(config.arguments)) as Record<string, unknown>;
      } catch {
        throw new Error('MCP arguments must be valid JSON');
      }
    } else if (input && typeof input === 'object') {
      toolArgs = input as Record<string, unknown>;
    }

    const response = await invokeMcpServer(
      serverUrl,
      'tools/call',
      { name: toolName, arguments: toolArgs },
      authToken
    );
    if (!response.ok) {
      throw new Error(response.error || 'MCP tools/call failed');
    }
    return response.result;
  }

  private async executeTransformTool(config: any, input: any): Promise<any> {
    if (!config.transform) {
      return input;
    }

    try {
      const fn = new Function('input', `return (async () => { ${config.transform} })()`);
      return await fn(input);
    } catch (error) {
      throw new Error(`Transform error: ${error instanceof Error ? error.message : 'Unknown'}`);
    }
  }

  private executeConditionalTool(config: any, input: any): any {
    // Simple conditional: if input meets condition, return it, otherwise null
    if (!config.condition) return input;

    try {
      const fn = new Function('input', `return ${config.condition}`);
      return fn(input) ? input : null;
    } catch (error) {
      throw new Error(`Conditional error: ${error instanceof Error ? error.message : 'Unknown'}`);
    }
  }

  private async executeAction(node: Node, input: any): Promise<any> {
    const { subtype, config } = node;

    switch (subtype) {
      case 'notification':
        console.log(`[Notification] ${config.message || 'No message'}`);
        return { notified: true, message: config.message };

      case 'log':
        console.log(`[Log] ${config.message || JSON.stringify(input)}`);
        return { logged: true };

      case 'webhook':
        if (this.options.dryRun) {
          return { sent: true, simulated: true, url: substituteSecrets(config.url || ''), payload: input };
        }
        if (!config.url) throw new Error('Webhook URL not configured');
        await fetch(substituteSecrets(config.url), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        });
        return { sent: true };

      case 'storage': {
        const key = config.key || `workflow_output_${Date.now()}`;
        if (this.options.dryRun && this.options.skipPersistence) {
          return { stored: true, simulated: true, key, preview: input };
        }
        localStorage.setItem(key, JSON.stringify(input));
        return { stored: true, key };
      }

      default:
        throw new Error(`Unknown action type: ${subtype}`);
    }
  }

  private async executeGmailInboundTrigger(config: Record<string, unknown>): Promise<unknown> {
    const useApi = config.source === 'gmail_api' || config.useGmailApi === true;
    const query = String(config.gmailQuery || 'is:unread newer_than:7d');

    if (useApi) {
      const event = await fetchLatestGmailApiMessage(query);
      if (!event) {
        throw new Error(`No Gmail messages for query: ${query}`);
      }
      return { ...event, type: 'gmail_inbound', source: 'gmail_api' };
    }

    const inboxId = String(config.inboxId || '').trim();
    if (!inboxId || inboxId === 'pending-inbox') {
      throw new Error('Gmail inbox not configured — plan again with webhook server running.');
    }

    const event = await fetchLatestGmailEvent(inboxId);
    if (!event) {
      throw new Error(
        'No email in Gmail inbox yet — POST a test payload to your webhook URL or use Dry Run first.'
      );
    }
    return { ...event, type: 'gmail_inbound', inboxId };
  }

  private simulatedHttpResponse(url: string): Record<string, unknown> {
    if (url.includes('openai.com')) {
      return {
        choices: [{ message: { content: '[Dry run] Simulated AI completion.' } }],
      };
    }
    if (url.includes('linkedin.com')) {
      return { applied: true, simulated: true, jobId: 'dry-run-job' };
    }
    return { ok: true, simulated: true };
  }
}